import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import { getCompanySites, addCompanySite } from '../api/crmApi';
import type { CreateCompanySiteInput } from '../types';

export const companySiteKeys = {
  all: ['crm-company-sites'] as const,
  list: (companyId: string) => [...companySiteKeys.all, companyId] as const,
};

/**
 * List a company's sites. retry:false because the backend route + company_sites
 * table (migration 0273) land separately — until then the request 404s and the
 * panel shows its empty state rather than a spinner or error toast.
 */
export function useCompanySites(companyId: string) {
  return useQuery({
    queryKey: companySiteKeys.list(companyId),
    queryFn: () => getCompanySites(companyId),
    staleTime: 2 * 60 * 1000,
    enabled: !!companyId,
    retry: false,
  });
}

export function useAddCompanySite(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanySiteInput) => addCompanySite(companyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companySiteKeys.list(companyId) });
      toast.success(i18next.t('crm:companies.sites.toast.added'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.sites.toast.failedAdd'), { description: error.message });
    },
  });
}
